import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { CheckCircle2, Loader2, Mic2, Trophy, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type ShowcaseSubmission,
  fetchTournamentDetail,
  fetchTournamentShowcase,
  postShowcaseSubmission,
  postShowcaseUploadUrl,
  postShowcaseVote,
  uploadShowcaseFile,
} from '~/api/tournaments';
import { getSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { formatCountdown, useCountdown } from '~/ui/Countdown';
import { VoteCardStack, type VoteResult } from '~/ui/VoteCardStack';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Button } from '~/ui/components/button';
import { Input } from '~/ui/components/input';

export const Route = createFileRoute('/tournaments/$id/showcase')({
  head: ({ params }) =>
    buildHead({
      title: 'Tournament Showcase',
      description: 'Listen and vote on tournament showcase submissions.',
      path: `/tournaments/${params.id}/showcase`,
    }),
  beforeLoad: async ({ location }) => {
    const session = await getSession();
    if (!session) {
      throw redirect({
        to: '/auth/sign-in',
        search: { next: location.pathname },
      });
    }
  },
  component: TournamentShowcasePage,
});

const ACCEPT = '.mp3,.wav,.ogg,audio/mpeg,audio/wav,audio/ogg';
const MAX_MB = 50;

type UploadStage = 'idle' | 'picked' | 'uploading' | 'done' | 'error';

function contentTypeOf(file: File): string {
  if (file.type) return file.type;
  if (file.name.endsWith('.mp3')) return 'audio/mpeg';
  if (file.name.endsWith('.wav')) return 'audio/wav';
  if (file.name.endsWith('.ogg')) return 'audio/ogg';
  return 'audio/mpeg';
}

function UploadWidget({
  tournamentId,
  onDone,
}: {
  tournamentId: string;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [stage, setStage] = useState<UploadStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onFile = useCallback(
    (picked: File) => {
      setError(null);
      if (picked.size > MAX_MB * 1024 * 1024) {
        setError(`File is too big (max ${MAX_MB} MB)`);
        return;
      }
      if (!/^audio\//.test(picked.type) && !/\.(mp3|wav|ogg)$/i.test(picked.name)) {
        setError('Pick an audio file (.mp3, .wav, or .ogg)');
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(picked);
      setFile(picked);
      setPreviewUrl(url);
      setStage('picked');
      const probe = new Audio(url);
      probe.addEventListener('loadedmetadata', () => {
        setDurationSec(Math.round(probe.duration));
      });
    },
    [previewUrl],
  );

  const upload = async () => {
    if (!file) return;
    setStage('uploading');
    setError(null);
    try {
      const ct = contentTypeOf(file);
      const { url, key } = await postShowcaseUploadUrl(tournamentId, ct);
      await uploadShowcaseFile(url, file);
      await postShowcaseSubmission(tournamentId, {
        key,
        title: title.trim() || undefined,
        durationSec: durationSec ?? undefined,
      });
      setStage('done');
      onDone();
    } catch (e) {
      setStage('error');
      setError((e as Error).message);
    }
  };

  const reset = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setDurationSec(null);
    setTitle('');
    setStage('idle');
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  if (stage === 'done') {
    return (
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center gap-3">
        <CheckCircle2 className="size-5 text-emerald-400 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-medium text-emerald-300">Track uploaded</div>
          <div className="text-xs text-muted-foreground">
            Your submission is live. Re-upload any time before the window closes.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={reset}>
          Replace
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="text-sm font-medium">Upload your showcase track</div>
      <label
        className={`block rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const dropped = e.dataTransfer.files[0];
          if (dropped) onFile(dropped);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const picked = e.target.files?.[0];
            if (picked) onFile(picked);
          }}
        />
        <Upload className="size-6 mx-auto text-muted-foreground mb-2" />
        <div className="text-sm">
          {file ? (
            <span className="font-mono">{file.name}</span>
          ) : (
            <>
              <span className="font-medium">Drop your track</span> or click to pick
            </>
          )}
        </div>
        {file && (
          <div className="text-xs text-muted-foreground mt-1">
            {(file.size / 1024 / 1024).toFixed(1)} MB
            {durationSec != null && ` - ${durationSec}s`}
          </div>
        )}
      </label>

      {previewUrl && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Preview</div>
          <WaveformPlayer src={previewUrl} />
        </div>
      )}

      {file && (
        <div className="space-y-1">
          <label htmlFor="showcase-title" className="text-xs text-muted-foreground block">
            Title (optional)
          </label>
          <Input
            id="showcase-title"
            placeholder="my track name"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={upload} disabled={!file || stage === 'uploading'}>
          {stage === 'uploading' ? (
            <>
              <Loader2 className="size-4 animate-spin" /> Uploading...
            </>
          ) : (
            <>
              <Upload className="size-4" /> Submit
            </>
          )}
        </Button>
        {file && stage !== 'uploading' && (
          <Button variant="outline" onClick={reset}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

// Read-only leaderboard row (shown after showcase closes / results are in)
function LeaderboardRow({ sub }: { sub: ShowcaseSubmission }) {
  return (
    <li className="rounded-md border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          {sub.finalRank !== null && (
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              #{sub.finalRank}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{sub.label}</span>
            {sub.isOwn && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-violet-400 border border-violet-500/40 rounded px-1 py-0.5">
                your track
              </span>
            )}
          </div>
          {sub.handle && (
            <div className="text-xs text-muted-foreground">
              <Link
                to="/$username"
                params={{ username: sub.handle }}
                className="hover:underline hover:text-foreground"
              >
                @{sub.handle}
              </Link>
            </div>
          )}
        </div>
        <div className="text-right space-y-1">
          <div className="font-mono text-lg font-bold tabular-nums">
            {Number(sub.score).toFixed(1)}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono uppercase">score</div>
        </div>
      </div>
      <WaveformPlayer src={sub.audioUrl} height={40} />
      {sub.myScore != null && (
        <div className="text-xs text-muted-foreground">You rated: {sub.myScore}/5</div>
      )}
    </li>
  );
}

function TournamentShowcasePage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: detail } = useQuery({
    queryKey: ['tournament', id],
    queryFn: () => fetchTournamentDetail(id),
  });

  const {
    data: showcase,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tournament-showcase', id],
    queryFn: () => fetchTournamentShowcase(id),
    refetchInterval: 15_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tournament-showcase', id] });
  }, [queryClient, id]);

  const endsAt = showcase?.endsAt ?? null;
  const countdown = useCountdown(showcase?.status === 'showcase' ? endsAt : null);
  const isShowcaseOpen = showcase?.status === 'showcase' && (countdown ?? 1) > 0;

  const isEntrant = detail?.registered ?? false;

  const isFinished =
    showcase !== undefined &&
    (showcase.status === 'finished' ||
      showcase.status === 'in_progress' ||
      showcase.submissions.some((s) => s.finalRank !== null));

  // Build entries for card stack (only when voting is live)
  const voteEntries = (showcase?.submissions ?? []).map((s) => ({
    id: s.id,
    label: s.label,
    audioUrl: s.audioUrl,
    durationSec: s.durationSec,
    isOwn: s.isOwn,
  }));

  const handleVoteSubmit = async (votes: VoteResult[]) => {
    // Showcase vote is per-submission; send them all in parallel using the
    // existing single-vote endpoint, then invalidate.
    await Promise.all(votes.map((v) => postShowcaseVote(id, v.submissionId, v.score)));
    invalidate();
  };

  if (isLoading) {
    return (
      <section className="mx-auto max-w-lg px-4 py-12">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </section>
    );
  }

  if (error || !showcase) {
    return (
      <section className="mx-auto max-w-lg px-4 py-12 space-y-3">
        <h1 className="text-2xl font-bold">Showcase not available</h1>
        <p className="text-sm text-muted-foreground">
          {error
            ? (error as Error).message
            : 'The showcase for this tournament could not be loaded.'}
        </p>
        <Link to="/tournaments/$id" params={{ id }} className="underline text-sm">
          Back to tournament
        </Link>
      </section>
    );
  }

  return (
    <article className="mx-auto max-w-lg px-4 py-12 space-y-8">
      <div>
        <Link
          to="/tournaments/$id"
          params={{ id }}
          className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          {`<- ${detail?.name ?? 'tournament'}`}
        </Link>
      </div>

      <header className="space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <Mic2 className="size-6 text-violet-400 mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold tracking-tight">
              {detail && !detail.bracketEnabled ? 'Weekly Showcase' : 'Showcase'}
            </h1>
            {detail && (
              <p className="text-sm text-muted-foreground">
                {detail.genreName} -{' '}
                {showcase.submissions.length === 1
                  ? '1 track'
                  : `${showcase.submissions.length} tracks`}
              </p>
            )}
          </div>
        </div>

        {detail && !detail.bracketEnabled && isShowcaseOpen && (
          <p className="text-sm text-muted-foreground">
            Weekly showcase - one track decides the week. The producer with the most votes when this
            closes wins.
          </p>
        )}
        {detail?.bracketEnabled && isShowcaseOpen && (
          <p className="text-sm text-muted-foreground">
            3-day community vote. Top track earns the crowd_favorite achievement, then the bracket
            runs.
          </p>
        )}

        {isShowcaseOpen && countdown !== null && (
          <div className="flex items-center gap-2 text-sm text-violet-300">
            <span>Voting closes in</span>
            <span className="font-mono font-bold">{formatCountdown(countdown)}</span>
          </div>
        )}

        {!isShowcaseOpen && !isFinished && (
          <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
            {detail && !detail.bracketEnabled
              ? 'The showcase window has closed. Winner will be announced shortly.'
              : 'The showcase window has closed. Results will appear once the bracket begins.'}
          </div>
        )}

        {isFinished && (
          <div className="rounded-lg border border-violet-500/40 bg-violet-500/5 px-4 py-3 text-sm text-violet-300 flex items-center gap-2">
            <Trophy className="size-4 shrink-0" />
            {detail && !detail.bracketEnabled
              ? 'Final results - top track wins the weekly.'
              : 'Final results - the bracket is now live.'}
          </div>
        )}
      </header>

      {isEntrant && isShowcaseOpen && <UploadWidget tournamentId={id} onDone={invalidate} />}

      {showcase.submissions.length === 0 && (
        <p className="text-sm text-muted-foreground">No tracks uploaded yet. Check back soon.</p>
      )}

      {/* Live voting: card stack */}
      {showcase.submissions.length > 0 && isShowcaseOpen && showcase.canVote && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Rate the tracks</h2>
          <VoteCardStack
            entries={voteEntries}
            onSubmit={handleVoteSubmit}
            submitLabel="Submit votes"
          />
        </section>
      )}

      {/* Finished / closed: leaderboard list */}
      {showcase.submissions.length > 0 && (isFinished || !isShowcaseOpen) && (
        <section className="space-y-3">
          {isFinished && <h2 className="text-xl font-semibold">Leaderboard</h2>}
          <ul className="space-y-3">
            {showcase.submissions.map((sub) => (
              <LeaderboardRow key={sub.id} sub={sub} />
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
